/**
 * One `sent_sms` row as the guest detail reads it — `GET /api/v1/sms-sent/{eventId}/guest/{guestId}`
 * (main-backend `SentSmsResource`, WORKFLOWS_READ; row scope as the sent-mail twin).
 *
 * The consumer writes the row (PENDING before the AWS call, then SENT / DRYRUN /
 * FAILED / SKIPPED); the rendered body is NOT stored. `to` is the E.164 number on
 * a guest-mode row, masked by the backend (`+49171*****78`) unless the viewer's
 * field mask holds BOTH `mobile` and `phone` (main-backend B1; audit r1 P3d) —
 * the cockpit shows it as delivered and never unmasks. Every key is optional on the read side: a newer backend may add
 * keys, an older one has no route at all.
 *
 * MIRROR: main-consumer `src/lib/sent-sms-repo.ts` (writer), main-backend
 * `org/nikita/app/sms_sent/SentSms.java`.
 */
export type SentSmsStatus = "PENDING" | "SENT" | "DRYRUN" | "FAILED" | "SKIPPED";

export interface SentSms {
  /** Row id (hex) — `SentSmsDTO.sentSmsId`; `id` tolerated. */
  sentSmsId?: string;
  id?: string;
  status?: SentSmsStatus | string;
  /**
   * The pre-debit state (wave D, C2 — SMS are taken from the allowance BEFORE the send):
   * RESERVING → DEBITED (paid) → REFUNDING → REFUNDED (AWS refused it), NONE (never debited),
   * DONE (a platform-paid verification code). Absent on rows skipped before any debit.
   */
  chargeState?: "RESERVING" | "DEBITED" | "REFUNDING" | "REFUNDED" | "NONE" | "DONE" | string | null;
  /** Billed parts (GSM-7 160/153, UCS-2 70/67); 0 on a row that never went out. */
  parts?: number;
  encoding?: "GSM-7" | "UCS-2" | string;
  recipientMode?: "guest" | "fixed" | "otp" | string;
  /** E.164, or masked — see above. */
  to?: string | null;
  /** SKIPPED rows: one of the skip codes (assumptions "Reason codes"). */
  skipReason?: string | null;
  /** FAILED rows (and DRYRUN refusals): one of the fail codes. */
  failReason?: string | null;
  workflowId?: string | null;
  /** ISO-8601. */
  createdAt?: string | null;
  // ── The event's SMS log only (SMS plugin wave, BE-5: `GET /sms-sent/{eventId}`) ──
  // JSON-B drops null keys, so every one of these may be absent.
  /** The automation's name; `null` / absent when it was deleted since. */
  workflowName?: string | null;
  /** The guest a guest-mode row went to; `null` on a fixed (team number) row. */
  guestId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  /**
   * The viewer's field scope blanked at least ONE name field (the guest is in
   * row scope); a readable part is still sent and is still the name.
   */
  nameHidden?: boolean;
  /** The guest no longer exists (only shown to a row-unrestricted viewer). */
  guestDeleted?: boolean;
  /**
   * A row of a manual send from the guest list (SMS manual send, B-3): the
   * send's name and job id from `sms_manual_sends`. `{}` (no keys) once that
   * record expired (91 days) — still manual, the name is gone.
   */
  manual?: SentSmsManual | null;
}

export interface SentSmsManual {
  sendName?: string | null;
  jobId?: string | null;
}

/**
 * One guest of a manual send's per-send view (B-3): a `sent_sms` row
 * (`source: "row"`) or a recipient the job skipped before any row was written
 * (`source: "skip"`, `status: "SKIPPED"`, no row columns). One item per guest;
 * a row wins over a skip.
 */
export interface SmsSendViewItem extends SentSms {
  source?: "row" | "skip" | string;
}

/**
 * The per-send view of ONE manual send — `GET /api/v1/sms-sent/{eventId}?send=<jobId>`
 * (SMS manual send, B-3). One page (a send reaches at most 500 guests): the
 * send's rows and skips, the send's name and size (`sms_manual_sends`, 91
 * days) and the job's counters. The job row goes 7 days after the send ended
 * (`jobAvailable: false` — "details no longer available"): the rows stay, the
 * skips and counts are gone. For a row-restricted viewer every job counter
 * (`recipients`, `total`, the counts, `smsSkipCounts`) is `null` — the job's
 * totals ignore its scope (audit r1 S1-M1 / FD-1); the page counts the rows.
 *
 * MIRROR: main-backend `org/nikita/app/sms_sent/SentSmsSendView.java`.
 */
export interface SmsSendView {
  jobId: string | null;
  sendAvailable: boolean;
  sendName: string | null;
  recipients: number | null;
  createdAt: string | null;
  jobAvailable: boolean;
  status: string | null;
  total: number | null;
  processedCount: number | null;
  failedCount: number | null;
  cancelledCount: number | null;
  skippedCount: number | null;
  dryrunCount: number | null;
  smsSkipCounts: Record<string, number> | null;
  smsHaltReason: string | null;
  smsHaltAt: string | null;
  /**
   * Why a FAILED send ended, as a CODE (never the job's raw `errorMessage`):
   * a fan-out refusal before any guest (`smsManualDailyLimit`,
   * `smsManualPluginOff`, …), the backend's late failure
   * (`smsManualCouldNotStart` / `smsManualStartUnknown`) or a dead fan-out.
   * `null` when the job did not fail, or names no reason.
   */
  endReason: string | null;
  items: SmsSendViewItem[];
}

/**
 * One page of the event's SMS log (BE-5), newest first. `nextCursor` is set
 * whenever the server may hold more — also after a page it had to cut short
 * (it scans at most 500 rows per call), so an EMPTY page with a cursor is not
 * the end; `null` means the data ran out.
 */
export interface SentSmsPage {
  items: SentSms[];
  nextCursor: string | null;
}

/** The row's id under either spelling; "" when the backend sent none. */
export function sentSmsIdOf(row: SentSms): string {
  return row.sentSmsId ?? row.id ?? "";
}
